import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { catchError, map, defaultIfEmpty } from 'rxjs/operators';
import { of } from 'rxjs';
import { RaAngularMyidLoginService } from '@wdpr/ra-angular-myid-login';

import { SoloService } from '@finder/shared/services/solo-service/solo.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { ConfigService } from '@finder/core/config.service';
import { STATUS_CODES } from '@finder/shared/constants/response-code.constants';
import { SsrService } from '@finder/shared/services/ssr/ssr.service';
import { LocalStorageService } from '@finder/shared/services/local-storage/local-storage.service';

@Injectable({
    providedIn: 'root'
})
export class MyIdGuard implements CanActivate {

    constructor(
        private soloService: SoloService,
        private windowRef: WindowRef,
        private config: ConfigService,
        private loginService: RaAngularMyidLoginService,
        private ssrService: SsrService,
        private localStorageService: LocalStorageService,
    ) { }

    canActivate(route: ActivatedRouteSnapshot, routerState: RouterStateSnapshot) {
        // Check the env var to see if gating is disabled
        if (this.config.getValue('disableMyIDGating') === true) {
            return of(true);
        }

        // SSR: don't run login; client enforces after hydration
        if (this.ssrService.isSSR()) {
            return of(true);
        }

        // ACME auth flow. The MyID login block is configured once at bootstrap
        // (app.config.ts APP_INITIALIZER); if that didn't happen we can't gate, so block.
        if (this.config.getValue('acmeEnabled') === true) {
            if (!this.loginService.getConfig()?.authorizationUrl) {
                return of(false);
            }

            // No token → send the user to MyID with the INTENDED destination as `state`.
            // We pass state EXPLICITLY rather than letting initLogin() redirect, because it calls
            // redirectToMyIdLogin() with no arg and the block then falls back to
            // window.location.href — which, by the time its async code-challenge step reads it, has
            // been reverted to '/' (this guard returns false → Angular cancels the navigation).
            // redirectToMyIdLogin() reads `raLoginConfig`, which only initLogin() assigns, so prime
            // it from the configured block first (otherwise it throws on `responseType`).
            if (!this.localStorageService.getItem('authToken')) {
                (this.loginService as unknown as { raLoginConfig: unknown }).raLoginConfig =
                    this.loginService.getConfig();
                this.loginService.redirectToMyIdLogin(`${this.windowRef.nativeWindow.location.origin}${routerState.url}`);

                return of(false);
            }

            // Token present → let initLogin() validate it (emits → allow; error → block).
            return this.loginService.initLogin().pipe(
                map(() => true),
                defaultIfEmpty(false),
                catchError(() => of(false))
            );
        }

        // SOLO (legacy — rollback path, unchanged)
        // Call the API for gating check
        return this.soloService.validateToken()
            .pipe(
                map(token => token.valid),
                catchError(error => {
                    if (error && error.status === STATUS_CODES.UNAUTHORIZED) {
                        // Redirect to myid login
                        this.windowRef.nativeWindow.location.href =
                            this.soloService.getLoginRedirectUrl(this.windowRef.nativeWindow.location.href);
                    }

                    return of(false);
                })
            );
    }
}
